import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { useMe, usePlatformConfig } from '../hooks/useApiQuery';
import { useRealtime } from '../hooks/useRealtime';
import { formatResumeTime } from '../lib/format';
import type { RealtimeEnvelope } from '../lib/realtime';
import type { KillSwitches } from '../lib/types';

/**
 * Top-of-screen banner shown when ANY kill switch is active — either
 * globally (admin paused the whole platform) or just for this user
 * (admin paused or banned them individually).
 *
 * Per-user flags come from two sources, freshest wins:
 *   - on load → `/v1/me` `kill_switches` (a TanStack query, so the
 *     banner is correct on first paint, not only after a live event);
 *   - live    → the `kill_switch` realtime event, which carries the
 *     full per-user snapshot. Once one arrives it is the truth.
 *
 * The realtime event is NOT optional: `get_current_user` 403s a banned
 * user, so once `is_banned` flips, `/v1/me` is unreachable and can
 * never report the ban — the event is the only channel that still
 * delivers it. Global flags come from `/v1/platform-config`.
 *
 * The event is held in plain state and only ever replaced by a newer
 * event — no effect wipes it. An earlier version cleared it on every
 * `platform-config` refetch, which (since a `kill_switch` event also
 * triggers that refetch) made the banner flash on then vanish.
 *
 * The banner explains *what* is paused, not *why* — admins write the
 * "why" into a separate notify event when they care.
 */
export function KillSwitchBanner() {
  const { t, i18n } = useTranslation();
  const cfg = usePlatformConfig();
  const me = useMe();
  const locale = i18n.language.startsWith('ru') ? 'ru-RU' : 'en-US';

  // Latest `kill_switch` event payload — a full per-user snapshot
  // (`admin.py` publishes all six flags every time). Null until the
  // first event; then it wins over `/v1/me` as the freshest source.
  const [lastEvent, setLastEvent] = useState<KillSwitches | null>(null);
  useRealtime(
    (ch) => ch.endsWith(':notify'),
    (env: RealtimeEnvelope) => {
      if (env.type === 'kill_switch') {
        setLastEvent(env.payload as KillSwitches);
      }
    },
  );
  const ks = lastEvent ?? me.data?.kill_switches;

  // Trading splits per-bot. master = both venues; pm/hl scope a
  // single bot. Effective state OR's the global flag with the
  // per-user one at each level.
  const masterTrading = !!cfg.data?.trading_paused || !!ks?.trading_paused;
  const pmTrading = !!cfg.data?.pm_trading_paused || !!ks?.pm_trading_paused;
  const hlTrading = !!cfg.data?.hl_trading_paused || !!ks?.hl_trading_paused;
  const tradingPaused = masterTrading || pmTrading || hlTrading;
  const depositsPaused =
    !!cfg.data?.deposits_paused || !!ks?.deposits_paused;
  const withdrawalsPaused =
    !!cfg.data?.withdrawals_paused || !!ks?.withdrawals_paused;
  const isBanned = !!ks?.is_banned;

  const anyOn =
    tradingPaused || depositsPaused || withdrawalsPaused || isBanned;
  if (!anyOn) return null;

  // Name the frozen bot: master (or both per-bot flags) reads as
  // "all trading"; a single flag names just that venue.
  const tradingMsg =
    masterTrading || (pmTrading && hlTrading)
      ? t('kill_switch.trading')
      : pmTrading
        ? t('kill_switch.trading_pm')
        : t('kill_switch.trading_hl');

  // Auto circuit-breaker pause carries a scheduled resume — append it to
  // the PM line so the user knows when the bot comes back on its own. An
  // admin/global pause has no `pause` object, so this stays silent.
  const pmResume = me.data?.bots?.pm?.pause?.resume_at;
  const resumeSuffix =
    pmTrading && !masterTrading && !hlTrading && pmResume
      ? ' ' + t('kill_switch.resumes_at', { time: formatResumeTime(pmResume, locale) })
      : '';

  const messages: string[] = [];
  if (isBanned) messages.push(t('kill_switch.banned'));
  if (tradingPaused) messages.push(tradingMsg + resumeSuffix);
  if (depositsPaused) messages.push(t('kill_switch.deposits'));
  if (withdrawalsPaused) messages.push(t('kill_switch.withdrawals'));

  return (
    <div
      role="status"
      aria-live="polite"
      className={clsx(
        'sticky top-0 z-30 -mx-5 mb-3 border-b px-5 py-2.5 text-[12px] leading-snug',
        isBanned
          ? 'border-pb-danger/40 bg-[color-mix(in_oklab,var(--pb-danger)_18%,transparent)] text-pb-danger'
          : 'border-pb-warn/40 bg-[color-mix(in_oklab,var(--pb-warn)_14%,transparent)] text-pb-warn',
      )}
    >
      <p className="font-medium">{t('kill_switch.eyebrow')}</p>
      <ul className="mt-0.5 space-y-0.5">
        {messages.map((m) => (
          <li key={m}>· {m}</li>
        ))}
      </ul>
    </div>
  );
}
